<template>
  <div class="partnerContractInfo" v-loading="loading">
    <div class="partnerContractInfoTableScroll">
      <table class="partnerContractInfoTable">
        <thead>
          <tr>
            <th>序号</th>
            <th>合同编号</th>
            <th>授权等级</th>
            <th>授权模式</th>
            <th>授权状态</th>
            <th>授权起始日</th>
            <th>授权到期日</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <!-- 无数据时显示暂无数据，表头保留 -->
          <tr v-if="!contractList || !contractList.length">
            <td colspan="8" class="partnerContractInfoTableEmpty">暂无数据</td>
          </tr>
          <tr
            v-else
            v-for="(row, index) in tableRows"
            :key="`${index}-${row.contractCode || 'empty'}`"
            class="partnerContractInfoTableRow"
          >
            <td>{{ index + 1 }}</td>
            <td>{{ row.contractCode || "" }}</td>
            <td class="is-center">
              <img
                v-if="getAuthTypeIcon(row.contractLevel)"
                :src="getAuthTypeIcon(row.contractLevel)"
                :alt="getAuthLevelKey(row.contractLevel)"
                class="partnerContractInfoAuthIcon"
              />
              <span v-else-if="row.contractLevel != null">{{
                getAuthLevelKey(row.contractLevel)
              }}</span>
              <span v-else>--</span>
            </td>
            <td>{{ formatAuthMode(row.authMode) }}</td>
            <td>{{ formatAuthStatus(row.authStatus) }}</td>
            <td>{{ row.startTime || "" }}</td>
            <td>{{ row.endTime || "" }}</td>
            <td>
              <a
                v-if="row.contractCode"
                class="partnerContractInfoAction"
                href="javascript:;"
                @click.prevent="onViewContract(row)"
              >
                查看合同
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import L1 from "@/assets/images/L1.png";
import L2 from "@/assets/images/L2.png";
import L3 from "@/assets/images/L3.png";
import L4 from "@/assets/images/L4.png";

const LEVEL_ICON_MAP = { L1, L2, L3, L4 };

/**
 * 合作伙伴详情 - 合同信息（只读）
 * 对应设计稿：9 列合同表格 + 查看合同
 */
export default {
  name: "PartnerContractInfo",
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      levelIconMap: LEVEL_ICON_MAP,
    };
  },
  computed: {
    contractList() {
      const d = this.data || {};
      if (Array.isArray(d.contractList) && d.contractList.length) {
        return this.normalizeList(d.contractList);
      }
      if (Array.isArray(d.contracts) && d.contracts.length) {
        return this.normalizeList(d.contracts);
      }
      return [];
    },
    /** 至少保留 5 行，与设计稿空白行一致 */
    tableRows() {
      const list = this.contractList || [];
      if (!list.length) return [];
      const minRows = list.length;
      const rows = list.map((item) => ({ ...item }));
      while (rows.length < minRows) {
        rows.push({});
      }
      return rows;
    },
  },
  methods: {
    /** 解析授权等级数值或字符为 L1-L4 */
    getAuthLevelKey(level) {
      if (level === 0) return "L1";
      if (level === 1) return "L2";
      if (level === 2) return "L3";
      if (level === 3) return "L4";
      if (level !== undefined && level !== null && level !== "") {
        const raw = String(level).toUpperCase().trim();
        if (["L1", "L2", "L3", "L4"].includes(raw)) return raw;
      }
      return "";
    },
    getAuthTypeIcon(level) {
      const key = this.getAuthLevelKey(level);
      return key ? this.levelIconMap[key] : "";
    },
    /** 格式化授权模式：0-定额永许、1-会员无限 */
    formatAuthMode(mode) {
      if (mode === 0) return "定额永许";
      if (mode === 1) return "会员无限";
      if (mode === undefined || mode === null || mode === "") return "";
      return String(mode);
    },
    /** 格式化授权状态：1-有效、2-休眠 */
    formatAuthStatus(status) {
      if (status === 1) return "有效";
      if (status === 2) return "休眠";
      if (status === undefined || status === null || status === "") return "";
      return String(status);
    },
    normalizeList(list) {
      return list.map((item) => ({
        contractCode: item.contractCode ?? item.contractNo ?? item.no,
        contractLevel:
          item.contractLevel ?? item.authType ?? item.authLevel ?? item.level,
        authMode: item.authMode,
        authStatus: item.authStatus,
        startTime: item.startTime ?? item.signDate,
        endTime: item.endTime ?? item.expireDate,
        authContractFile: item.authContractFile,
        otherContractFile: item.otherContractFile,
        thirdPartyContractFile: item.thirdPartyContractFile,
      }));
    },

    onViewContract(row) {
      let url =
        row.authContractFile ||
        row.otherContractFile ||
        row.thirdPartyContractFile;
      if (!url) {
        this.$message.warning("暂无合同文件");
        return;
      }
      // 处理可能存在逗号分隔的多个文件或 JSON 数组包裹的情况，提取出第一个文件的真实 URL
      if (typeof url === "string") {
        url = url.trim();
        if (url.startsWith("[") && url.endsWith("]")) {
          try {
            const arr = JSON.parse(url);
            if (Array.isArray(arr) && arr.length) {
              url = arr[0];
            }
          } catch (e) {
            url = url.replace(/[\[\]"']/g, "").split(",")[0].trim();
          }
        } else if (url.includes(",")) {
          url = url.split(",")[0].trim();
        }
      }
      if (!url) {
        this.$message.warning("暂无合同文件");
        return;
      }
      // 使用正则检查 URL 路径中或参数中是否包含 .pdf（支持含 query 参数如 filename=xxx.pdf）
      if (/\.pdf(\?|&|#|$)/i.test(url)) {
        this.$router.push({
          path: "/pdf/index",
          query: { url, name: row.contractCode || "合同" },
        });
      } else {
        window.open(url, "_blank");
      }
    },
  },
};
</script>

<style lang="less" scoped>
.partnerContractInfo {
  padding: 32px;
  background: #fff;
  min-height: 300px;
}
.partnerContractInfoTableScroll {
  overflow-x: auto;
}
.partnerContractInfoTable {
  width: 100%;
  min-width: 960px;
  border-collapse: collapse;
  font-size: 14px;
  text-align: left;
  thead {
    background: #f9fafb;
    color: #1e293b;
    font-weight: 700;
    border-top: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
  }
  th {
    padding: 12px 16px;
    border-right: 1px solid #e5e7eb;
    white-space: nowrap;
    &:last-child {
      border-right: none;
    }
  }
  td {
    padding: 16px;
    color: #475569;
    border-right: 1px solid #f3f4f6;
    border-bottom: 1px solid #f3f4f6;
    &:last-child {
      border-right: none;
    }
    &.is-center {
      text-align: center;
    }
  }
}
.partnerContractInfoTableRow:hover td {
  background: #f9fafb;
}
.partnerContractInfoAuthIcon {
  display: inline-block;
  height: 24px;
  width: auto;
  vertical-align: middle;
}
.partnerContractInfoAction {
  color: #00bcd4;
  text-decoration: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}
.partnerContractInfoTableEmpty {
  padding: 48px !important;
  color: #94a3b8;
  font-size: 14px;
  background: #fff;
  text-align: center;
}
</style>
